// Root app — router + shell composition
import React, { useState, useEffect } from "react";
import { navigate, useMe, apiDelete } from "./api.jsx";
import { AuditPage } from "./audit.jsx";
import { Dashboard } from "./dashboard.jsx";
import { Analyzer } from "./analyzer.jsx";
import { Pipeline } from "./pipeline.jsx";
import { ResultsView } from "./results.jsx";
import { Landing } from "./landing.jsx";
import { AuthPage } from "./auth.jsx";
import { AdminOverview, AdminUsers, AdminAnalytics, AdminSettings } from "./admin.jsx";
import { InvestorHub, SubmissionDetail, MySubmissions } from "./investor.jsx";
import { AccountSettings, ChangePassword } from "./account.jsx";
import { Sidebar, Topbar } from "./nav.jsx";
import { Tweaks } from "./tweaks.jsx";
import { CommandPalette, IASLegend, DemoBanner } from "./overlays.jsx";

const ROUTES = {
  audit:           { label: "Audit & direction", chrome: true, section: "design", render: (s) => <AuditPage/> },
  dashboard:       { label: "Dashboard", chrome: true, section: "workspace", render: (s) => <Dashboard variant={s.dashVariant}/> },
  analyzer:        { label: "New analysis", chrome: true, section: "workspace", render: () => <Analyzer/> },
  pipeline:        { label: "Pipeline", chrome: true, section: "workspace", render: () => <Pipeline/> },
  "investor-hub":  { label: "Investor hub", chrome: true, section: "workspace", render: () => <InvestorHub/> },
  submission:      { label: "Submission", chrome: true, section: "workspace", render: () => <SubmissionDetail/> },
  "my-submissions": { label: "My submissions", chrome: true, section: "workspace", render: () => <MySubmissions/> },
  results:         { label: "Analysis view", chrome: true, section: "workspace", render: (s) => <ResultsView role={s.role} setRole={s.setRole}/> },
  library:         { label: "Library", chrome: true, section: "workspace", render: () => <LibraryStub/> },
  landing:         { label: "Landing", chrome: false, section: "design", render: () => <Landing/> },
  auth:            { label: "Auth", chrome: false, section: "design", render: () => <AuthPage/> },
  admin:           { label: "Admin · Overview",  chrome: true, section: "admin", render: () => <AdminOverview/> },
  "admin-users":   { label: "Admin · Users",     chrome: true, section: "admin", render: () => <AdminUsers/> },
  "admin-analytics": { label: "Admin · Analytics", chrome: true, section: "admin", render: () => <AdminAnalytics/> },
  "admin-settings":  { label: "Admin · Settings",  chrome: true, section: "admin", render: () => <AdminSettings/> },
  "account-settings": { label: "Account · Settings", chrome: true, section: "workspace", render: () => <AccountSettings/> },
  "change-password":  { label: "Account · Change password", chrome: true, section: "workspace", render: () => <ChangePassword/> },
};

function ViewAsBanner() {
  const me = useMe();
  const viewAs = me.data?.view_as;
  const [stopping, setStopping] = useState(false);
  if (!viewAs?.active) return null;

  const stop = async () => {
    setStopping(true);
    try {
      await apiDelete("/admin/view-as/");
      try {
        localStorage.removeItem("vz-route");
        localStorage.removeItem("vz-analysis-id");
        localStorage.removeItem("vz-submission-id");
      } catch { /* ignore */ }
      // Hard reload back to admin home so all caches refetch as the real user.
      location.href = "/";
    } catch (err) {
      alert(`Couldn't stop view-as: ${err.message || "unknown error"}`);
      setStopping(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 250,
        background: "linear-gradient(90deg, #fb923c, #f97316)",
        color: "#1f1300",
        padding: "10px 18px",
        fontSize: 13,
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 2px 8px rgba(249, 115, 22, 0.35)",
      }}
    >
      <span style={{
        fontFamily: "var(--ff-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        background: "rgba(0,0,0,0.18)",
        padding: "3px 8px",
        borderRadius: 3,
      }}>VIEW AS</span>
      <span>
        You're seeing the app as <strong>{me.data?.name || me.data?.email}</strong>.
        Real account: <strong>{viewAs.real_user_name}</strong> ({viewAs.real_user_email}).
      </span>
      <div style={{flex: 1}}/>
      <button
        type="button"
        onClick={stop}
        disabled={stopping}
        style={{
          background: "rgba(0,0,0,0.18)",
          color: "#1f1300",
          border: "1px solid rgba(0,0,0,0.25)",
          borderRadius: 3,
          padding: "5px 12px",
          fontSize: 12,
          fontWeight: 600,
          cursor: stopping ? "default" : "pointer",
          opacity: stopping ? 0.6 : 1,
        }}
      >
        {stopping ? "Returning…" : "Return to admin"}
      </button>
    </div>
  );
}

function LibraryStub() {
  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:10}}>Library</div>
          <h1 className="page-title">Every record, searchable.</h1>
          <p className="page-sub" style={{maxWidth:600, marginTop:10}}>
            Filtered, taggable archive of all analyses, decks and notes. Stubbed in this prototype.
          </p>
        </div>
      </div>
    </div>
  );
}

// Catches render-time errors in any route so a single broken screen
// doesn't black out the entire app (previously an uncaught exception
// would unmount the whole tree and leave the user staring at the body
// background).
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    try { console.error("Route render failed:", error, info); } catch {}
  }
  componentDidUpdate(prevProps) {
    if (prevProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      const msg = (this.state.error && this.state.error.message) || String(this.state.error);
      return (
        <div className="page route-enter">
          <div className="page-header">
            <div className="h-eyebrow" style={{marginBottom:10, color:"var(--bad)"}}>Something went wrong</div>
            <h1 className="page-title">This screen didn't render.</h1>
            <p className="page-sub" style={{marginTop:12, maxWidth:640}}>
              A JavaScript error stopped the page from loading. Most likely
              cause: missing or malformed data. You can go back and try a
              different record, or reload the app.
            </p>
            <pre style={{
              marginTop:20, padding:"14px 16px",
              background:"var(--surface-2)", border:"1px solid var(--line)",
              borderRadius:4, fontSize:12, whiteSpace:"pre-wrap",
              maxWidth:720, overflow:"auto", color:"var(--bad)",
            }}>{msg}</pre>
            <div className="row" style={{marginTop:20, gap:10}}>
              <button className="btn btn--ghost" onClick={() => navigate("dashboard")}>Go to dashboard</button>
              <button className="btn btn--primary" onClick={() => location.reload()}>Reload app</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  const initial = (() => {
    try { return localStorage.getItem("vz-route") || "landing"; } catch { return "landing"; }
  })();
  const qp = new URLSearchParams(location.search);
  const initialDemo = qp.get("demo") === "1";
  const initialRole = qp.get("as") === "founder" ? "founder" : "investor";

  const [route, setRoute] = useState(initial);
  const [dashVariant, setDashVariant] = useState("desk");
  const [role, setRole] = useState(initialRole);
  const [demoOn, setDemoOn] = useState(initialDemo);
  const [demoStep, setDemoStep] = useState(0);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [iasLegendOpen, setIasLegendOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("vz-route", route); } catch {}
  }, [route]);

  // Listen for navigate() calls from anywhere in the tree — the api.jsx
  // helper dispatches this event so every click in the app routes through
  // the same place instead of calling location.reload().
  useEffect(() => {
    const h = (e) => {
      const r = e?.detail?.route;
      if (r) setRoute(r);
    };
    window.addEventListener("vz:navigate", h);
    return () => window.removeEventListener("vz:navigate", h);
  }, []);

  // expose IAS legend trigger globally
  useEffect(() => {
    window.showIASLegend = () => setIasLegendOpen(true);
  }, []);

  // cmd-K
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdkOpen(o => !o);
      } else if (e.key === "Escape") {
        setCmdkOpen(false);
        setIasLegendOpen(false);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // demo body class
  useEffect(() => {
    document.body.classList.toggle("demo-on", demoOn);
  }, [demoOn]);

  // body data-section for admin chrome
  const cur = ROUTES[route] || ROUTES.landing;
  useEffect(() => {
    document.body.setAttribute("data-section", cur.section || "workspace");
  }, [cur.section]);

  const chrome = cur.chrome;
  const isAdmin = cur.section === "admin";

  const crumbs = route === "dashboard" ? ["Workspace", "Dashboard"] :
                  route.startsWith("admin") ? ["Administration", cur.label.replace("Admin · ","")] :
                  (route === "account-settings" || route === "change-password") ? ["Account", cur.label.replace("Account · ","")] :
                  route === "audit" ? ["Design", "Audit & direction"] :
                  cur.section === "design" ? ["Design", cur.label] :
                  ["Workspace", cur.label];

  const topActions = route === "dashboard" ? (
    <div className="seg" style={{marginLeft:12}}>
      <button className={dashVariant==="desk"?"on":""} onClick={()=>setDashVariant("desk")}>Desk</button>
      <button className={dashVariant==="overview"?"on":""} onClick={()=>setDashVariant("overview")}>Overview</button>
    </div>
  ) : isAdmin ? (
    <span className="admin-badge"><span style={{width:6,height:6,borderRadius:"50%",background:"var(--bad)"}}/>ADMIN</span>
  ) : null;

  return (
    <>
      <ViewAsBanner/>
      {demoOn && (
        <DemoBanner
          step={demoStep}
          setStep={setDemoStep}
          setRoute={setRoute}
          setRole={setRole}
          setDashVariant={setDashVariant}
          onExit={()=>setDemoOn(false)}
        />
      )}

      {(() => {
        // Force a remount when navigating to the same route with a different
        // selection (e.g. opening a different analysis while already on
        // the Results screen). Without this, React reuses the mounted
        // component, the "read localStorage on mount" effect doesn't re-run,
        // and the screen shows the previous record.
        let sel = "";
        try {
          if (route === "results") sel = localStorage.getItem("vz-analysis-id") || "";
          else if (route === "submission") sel = localStorage.getItem("vz-submission-id") || "";
        } catch { /* ignore */ }
        const instanceKey = route + ":" + sel;
        return chrome ? (
          <div className="app-shell">
            <Sidebar route={route} setRoute={setRoute}/>
            <Topbar crumbs={crumbs} actions={topActions} onSearch={()=>setCmdkOpen(true)}/>
            <div className="main">
              <RouteErrorBoundary routeKey={instanceKey}>
                <div key={instanceKey}>{cur.render({ dashVariant, role, setRole })}</div>
              </RouteErrorBoundary>
            </div>
          </div>
        ) : (
          <>
            <div style={{position:"fixed", bottom: 16, left:16, zIndex:200}}>
              <button className="btn btn--quiet" style={{background:"var(--surface)", border:"1px solid var(--line)", fontSize:12}} onClick={()=>setRoute("dashboard")}>← Back to workspace</button>
            </div>
            <RouteErrorBoundary routeKey={instanceKey}>
              <div key={instanceKey}>{cur.render({})}</div>
            </RouteErrorBoundary>
          </>
        );
      })()}

      <Tweaks/>
      {cmdkOpen && <CommandPalette onClose={()=>setCmdkOpen(false)} setRoute={setRoute} setRole={setRole} setDemoOn={setDemoOn}/>}
      {iasLegendOpen && <IASLegend onClose={()=>setIasLegendOpen(false)}/>}
    </>
  );
}
